import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/price";
import { ShoppingBag, Plus } from "lucide-react";
import EventImage from "@/components/EventImage";

const COLOR_SWATCH = { Black: "#1a1a1a", Natural: "#e8ddc7" };
const CAT_KEY = { apparel: "store_cat_apparel", drinkware: "store_cat_drinkware", goods: "store_cat_goods" };

const ProductCard = ({ p, i, onAdd }) => {
  const { t } = useLang();
  const colors = useMemo(() => [...new Set((p.variants || []).map((v) => v.color).filter(Boolean))], [p.variants]);
  const hasVariants = Array.isArray(p.variants) && p.variants.length > 0;
  const [color, setColor] = useState(colors[0] || "");
  const [size, setSize] = useState("");
  const sizes = useMemo(() => (p.variants || []).filter((v) => !color || v.color === color).map((v) => v.size), [p.variants, color]);
  const image = (p.color_images && color && p.color_images[color]) || p.image;
  const colorLabel = (c) => t(`color_${c.toLowerCase()}`) || c;

  const handleAdd = () => {
    if (hasVariants && ((colors.length && !color) || !size)) { toast.error(t("size_select_prompt")); return; }
    const variant = hasVariants ? (p.variants.find((v) => v.size === size && (!color || v.color === color))) : null;
    onAdd(p, variant ? { color: color || null, size, printful_variant_id: variant.printful_variant_id, image } : null);
  };

  return (
    <article data-testid={`product-card-${p.slug}`} className="group bg-[#121418] border border-[#272A34] hover:border-amber-500/50 rounded-lg overflow-hidden transition-all duration-300 hover:-translate-y-1 fade-up" style={{ animationDelay: `${i * 50}ms` }}>
      <Link to={`/store/${p.slug}`} data-testid={`product-link-${p.slug}`} className="block relative h-64 overflow-hidden">
        <EventImage src={image} alt={p.name} imgClassName="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        {p.featured && <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase bg-amber-500 text-stone-950 font-bold">★</span>}
        {p.edition === "swiss-pagan" && <span data-testid={`edition-tag-${p.slug}`} className="absolute bottom-3 left-3 px-2.5 py-1 rounded-full text-[9px] font-mono uppercase tracking-wider bg-amber-500/90 text-stone-950 font-bold backdrop-blur">{t("store_edition_badge")}</span>}
        {p.sabbat && <span data-testid={`limited-tag-${p.slug}`} className="absolute bottom-3 right-3 px-2.5 py-1 rounded-full text-[9px] font-mono uppercase tracking-wider bg-indigo-600/90 text-white font-bold backdrop-blur">{t("store_limited_badge")}</span>}
      </Link>
      <div className="p-5">
        <Link to={`/store/${p.slug}`} className="block">
          <h3 className="font-serif text-lg text-stone-100 leading-snug mb-2 group-hover:text-amber-100">{p.name}</h3>
          <p className="text-sm text-stone-400 line-clamp-2 mb-4">{p.description}</p>
        </Link>
        {hasVariants && colors.length > 0 && (
          <div className="mb-3">
            <label className="block text-[11px] font-mono uppercase tracking-wider text-stone-500 mb-1.5">{t("color_label")}</label>
            <div className="flex items-center gap-2" data-testid={`color-options-${p.slug}`}>
              {colors.map((c) => (
                <button key={c} type="button" data-testid={`color-${p.slug}-${c}`} onClick={() => { setColor(c); setSize(""); }} title={colorLabel(c)}
                  className={`w-7 h-7 rounded-full border-2 transition-all ${color === c ? "border-amber-500 scale-110" : "border-[#272A34] hover:border-stone-500"}`}
                  style={{ backgroundColor: COLOR_SWATCH[c] || "#888" }} />
              ))}
              <span className="text-xs text-stone-400 ml-1">{color ? colorLabel(color) : ""}</span>
            </div>
          </div>
        )}
        {hasVariants && (
          <div className="mb-4">
            <label className="block text-[11px] font-mono uppercase tracking-wider text-stone-500 mb-1.5">{t("size_label")}</label>
            <select data-testid={`size-select-${p.slug}`} value={size} onChange={(e) => setSize(e.target.value)} className="w-full bg-[#181A20] border border-[#272A34] rounded-md px-3 py-2 text-sm text-stone-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500">
              <option value="">{t("size_select_prompt")}</option>
              {sizes.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="font-serif text-xl text-amber-400">{formatPrice(p.price_cents, p.currency)}</span>
          <button data-testid={`add-to-cart-${p.slug}`} onClick={handleAdd} disabled={p.stock <= 0} className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-4 py-2 rounded-md text-sm transition-colors disabled:opacity-50">
            <Plus className="w-4 h-4" /> {p.stock > 0 ? t("add_to_cart") : t("out_of_stock")}
          </button>
        </div>
      </div>
    </article>
  );
};

export default function Store() {
  const { t } = useLang();
  const { add, setOpen } = useCart();
  const [cat, setCat] = useState("all");
  const { data: products = [], isLoading } = useQuery({ queryKey: ["products"], queryFn: async () => (await api.get("/store/products")).data });

  const onAdd = (p, variant) => { add(p, 1, variant); toast.success(t("added_to_cart")); setOpen(true); };

  const categories = useMemo(() => [...new Set(products.map((p) => p.category).filter(Boolean))], [products]);
  const filtered = products.filter((p) => (cat === "all" ? true : p.category === cat));

  return (
    <>
      <Seo title={t("store_title")} description={t("store_subtitle")} canonicalPath="/store" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="mb-10 border-b border-amber-900/30 pb-5">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-2 flex items-center gap-2"><ShoppingBag className="w-3.5 h-3.5" /> swiss-pagan.ch</div>
          <h1 className="font-serif text-4xl sm:text-5xl uppercase tracking-tight text-amber-100">{t("store_title")}</h1>
          <p className="text-stone-400 mt-3 max-w-2xl">{t("store_subtitle")}</p>
        </div>

        <div className="space-y-4 mb-8">
          {categories.length > 1 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="store-category-filter">
              <span className="text-[11px] font-mono uppercase tracking-wider text-stone-500 mr-1 hidden sm:inline">{t("store_category_label")}</span>
              <button data-testid="store-cat-all" onClick={() => setCat("all")}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${cat === "all" ? "bg-amber-700/80 text-amber-50 border-amber-500 font-semibold" : "bg-[#121418] text-stone-300 border-[#272A34] hover:border-amber-500/40"}`}>
                {t("store_cat_all")}
              </button>
              {categories.map((c) => (
                <button key={c} data-testid={`store-cat-${c}`} onClick={() => setCat(c)}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${cat === c ? "bg-amber-700/80 text-amber-50 border-amber-500 font-semibold" : "bg-[#121418] text-stone-300 border-[#272A34] hover:border-amber-500/40"}`}>
                  {t(CAT_KEY[c]) || c}
                </button>
              ))}
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{[...Array(6)].map((_, i) => <div key={i} className="h-96 bg-[#121418] border border-[#272A34] rounded-lg animate-pulse" />)}</div>
        ) : filtered.length === 0 ? (
          <p className="text-stone-400 text-center py-20" data-testid="store-empty">{t("store_empty")}</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p, i) => <ProductCard key={p.slug} p={p} i={i} onAdd={onAdd} />)}
          </div>
        )}
      </div>
    </>
  );
}
